import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import api from "@/lib/api";

const FLAG = { DE: "🇩🇪", SI: "🇸🇮", HR: "🇭🇷", SK: "🇸🇰", AT: "🇦🇹", PL: "🇵🇱", RO: "🇷🇴", BA: "🇧🇦", RS: "🇷🇸", IT: "🇮🇹", HU: "🇭🇺", BG: "🇧🇬", CZ: "🇨🇿", MK: "🇲🇰" };

export function ComplianceNewsList({ endpoint = "/employer/compliance-news", limit = 50, showFilter = false, reloadKey = 0 }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null); // { news, countries }
  const [filter, setFilter] = useState("ALL");
  useEffect(() => {
    setData(null);
    api.get(`${endpoint}?limit=${limit}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ news: [], countries: [] }));
  }, [endpoint, limit, reloadKey]);

  if (data === null)
    return <div className="py-6 text-center text-sm text-slate-400" data-testid="emp-news-loading">…</div>;
  const news = data.news || [];
  if (news.length === 0)
    return <div className="py-6 text-center text-sm text-slate-400" data-testid="emp-news-empty">{t("ai.newsEmpty")}</div>;

  const countries = (data.countries || []).filter((c) => news.some((n) => n.country === c));
  const shown = filter === "ALL" ? news : news.filter((n) => n.country === filter);

  return (
    <div data-testid="emp-news-wrap">
      {showFilter && countries.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="emp-news-filters">
          <button onClick={() => setFilter("ALL")} data-testid="emp-news-filter-ALL"
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === "ALL" ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {t("ai.filterAll")}
          </button>
          {countries.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`emp-news-filter-${c}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === c ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              <span>{FLAG[c] || "🏳️"}</span> {c}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2" data-testid="emp-news-list">
        {shown.map((n) => (
          <div key={n.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`emp-news-item-${n.id}`}>
            <div className="mb-2 flex items-center gap-2 text-xs">
              <span className="text-base">{FLAG[n.country] || "🏳️"}</span>
              {n.category && <span className="rounded-full bg-cyan/10 px-2 py-0.5 font-semibold uppercase tracking-wider text-cyan-dark">{n.category}</span>}
              {n.published_at && <span className="text-slate-400">{n.published_at}</span>}
            </div>
            <div className="font-heading text-base font-bold text-navy">{n.title}</div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{n.summary}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
              <span>{n.source_name}</span>
              {n.source_url && <a href={n.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-dark hover:underline">{t("tax.viewSource")} <ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
